/**
 * Lazy-loaded OCR service wrapping Tesseract.js.
 *
 * Worker scripts, WASM core, and language data are all fetched from jsDelivr
 * CDN (Tesseract.js v7 default) — no static assets are added to the Vite
 * build. The dynamic import below keeps the JS module itself out of the main
 * chunk as well, so users who never trigger OCR pay zero bundle cost.
 *
 * CDN vs self-hosting: Tesseract.js v7 defaults workerPath / corePath /
 * langPath to jsDelivr, so no explicit configuration is needed. Self-hosting
 * would require serving ~3 MB of WASM plus per-language .traineddata files
 * from nginx — overhead not justified for an optional feature used on demand.
 */

import type { Worker as TesseractWorker } from 'tesseract.js';
import { isReadableOcr } from './ocrQuality';

// ── Public types ──────────────────────────────────────────────────────────────

export type OcrProgressPhase =
  /** Fetching the language .traineddata file and initialising the WASM core. */
  | 'download'
  /** Actual text extraction running inside the Tesseract worker. */
  | 'recognize';

export interface OcrProgress {
  /** Status label emitted by Tesseract ("loading language traineddata", "recognizing text", …) */
  status: string;
  /** Fraction complete within the current phase, 0–1 */
  progress: number;
  /**
   * 'download' while fetching language data / initialising WASM (first call only per session).
   * 'recognize' during text extraction.
   * Callers can use this to drive two visually distinct states in the UI.
   */
  phase: OcrProgressPhase;
}

export interface OcrResult {
  text: string;
  /** Per-page confidence score from Tesseract, 0–100 */
  confidence: number;
  /**
   * True when the requested ISO 639-1 code had no Tesseract counterpart and
   * 'eng' was substituted. Reserved for telemetry wiring in Prompt 7.6.
   */
  usedFallback: boolean;
  /**
   * True when Tesseract returned something that `isReadableOcr` judged to be noise: `text`
   * is then empty, so callers show their existing "no text found" state. False for a real
   * result and for a blank image.
   */
  unreadable?: boolean;
}

/** Image sources accepted by Tesseract.js v7 in a browser context. */
export type OcrImageSource =
  | string
  | File
  | Blob
  | HTMLImageElement
  | HTMLCanvasElement
  | HTMLVideoElement
  | OffscreenCanvas;

export type OcrProgressCallback = (progress: OcrProgress) => void;

// ── Language mapping ──────────────────────────────────────────────────────────

/**
 * Single source of truth mapping app ISO 639-1 codes → Tesseract 4 lang codes.
 * Covers all entries in SUPPORTED_LANGUAGES from backend config.py.
 *
 * The mapping is EXPLICIT — never derive Tesseract codes at runtime. They do
 * not always match ISO 639-1 ("por" ≠ "pt"; "chi_sim" has no ISO equivalent).
 * Add a new row here whenever SUPPORTED_LANGUAGES gains a new language.
 */
const TESSERACT_LANG_MAP: Readonly<Record<string, string>> = {
  en: 'eng',
  ru: 'rus',
  fr: 'fra',
  es: 'spa',
  de: 'deu',
  pt: 'por',
  it: 'ita',
  zh: 'chi_sim',
  ja: 'jpn',
  ko: 'kor',
};

const FALLBACK_LANG = 'eng';

/**
 * Resolves an app ISO 639-1 code to a Tesseract lang code.
 * Returns FALLBACK_LANG for codes not present in TESSERACT_LANG_MAP or when
 * `isoCode` is absent (unauthenticated/incomplete-onboarding callers).
 */
export function resolveOcrLanguage(isoCode: string | null | undefined): {
  tesseractLang: string;
  usedFallback: boolean;
} {
  if (!isoCode) return { tesseractLang: FALLBACK_LANG, usedFallback: true };
  const mapped = TESSERACT_LANG_MAP[isoCode];
  if (!mapped) return { tesseractLang: FALLBACK_LANG, usedFallback: true };
  return { tesseractLang: mapped, usedFallback: false };
}

// ── Session-scoped worker cache ────────────────────────────────────────────────
//
// Workers are kept alive within the browser session to avoid re-downloading
// language packs on every OCR call — traineddata files can be tens of MB for
// CJK scripts and re-downloading each time is unacceptable on mobile.
// The Map is populated lazily; nothing is loaded on app startup.

type WorkerEntry = {
  worker: TesseractWorker;
  /**
   * Mutable pointer updated immediately before each worker.recognize() call.
   * The shared createWorker logger reads this to route events to the caller
   * that is currently active. Cleared after each call.
   */
  activeCallback: OcrProgressCallback | undefined;
  /** Promise-chain lock that serialises concurrent recognize() calls. */
  lock: Promise<void>;
};

// Stores creation *promises* (not resolved entries) so two simultaneous first
// calls for the same language share one download instead of starting two.
const workerPromises = new Map<string, Promise<WorkerEntry>>();

function classifyPhase(status: string): OcrProgressPhase {
  return status === 'recognizing text' ? 'recognize' : 'download';
}

async function getOrCreateWorker(
  tesseractLang: string,
  onDownloadProgress: OcrProgressCallback | undefined,
): Promise<WorkerEntry> {
  const cached = workerPromises.get(tesseractLang);
  if (cached) return cached;

  const creation = (async (): Promise<WorkerEntry> => {
    const { createWorker } = await import('tesseract.js');

    // entry.activeCallback is set here so the logger closure can reference it.
    // During createWorker() it points to the first caller's onDownloadProgress.
    const entry: WorkerEntry = {
      worker: null as unknown as TesseractWorker,
      activeCallback: onDownloadProgress,
      lock: Promise.resolve(),
    };

    entry.worker = await createWorker(tesseractLang, 1 /* OEM.LSTM_ONLY */, {
      logger: ({ status, progress }: { status: string; progress: number }) => {
        entry.activeCallback?.({ status, progress, phase: classifyPhase(status) });
      },
    });

    return entry;
  })();

  workerPromises.set(tesseractLang, creation);
  return creation;
}

// Free WASM heap when the page unloads; avoids accumulation in long sessions.
if (typeof window !== 'undefined') {
  window.addEventListener('unload', () => {
    workerPromises.forEach((promise) =>
      promise.then(({ worker }) => worker.terminate()).catch(() => {}),
    );
  });
}

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Recognize text in `image` using the Tesseract language pack for `isoCode`.
 *
 * `isoCode` should be the user's `target_language` (ISO 639-1, e.g. "ru", "ja").
 * The service resolves it to the correct Tesseract lang code via TESSERACT_LANG_MAP.
 * Unknown or missing codes fall back to English and set `usedFallback: true` in
 * the result.
 *
 * `onProgress` receives two distinct phases:
 *   phase: 'download'  — language pack fetch + WASM init (first call per language per session)
 *   phase: 'recognize' — text extraction
 *
 * Language data is cached for the browser session; subsequent calls for the
 * same language skip the download phase entirely and proceed directly to
 * recognition. Nothing is eagerly loaded on app startup.
 */
/**
 * PSM 7 (single text line) investigation:
 * Tested on narrow crops (aspect ratio ≥ 3:1, e.g. one captured text line).
 * Results were inconclusive — PSM 7 sometimes improved short-line accuracy but
 * regressed on slightly multi-line captures. Narrow crops often still contain
 * 2+ lines of text, making PSM 7 counterproductive. The default PSM (3, AUTO)
 * is therefore kept and PSM is reset to 3 after each narrow-crop call so the
 * worker state remains predictable for subsequent calls.
 */
const PSM_AUTO = 3;

export async function recognize(
  image: OcrImageSource,
  isoCode?: string | null,
  onProgress?: OcrProgressCallback,
  /** Tesseract page segmentation mode (PSM). Defaults to AUTO (3). */
  psm?: number,
): Promise<OcrResult> {
  const { tesseractLang, usedFallback } = resolveOcrLanguage(isoCode);
  const entry = await getOrCreateWorker(tesseractLang, onProgress);

  // Acquire the per-worker lock to serialise concurrent calls.
  // The lock is a promise chain: each caller stores the *previous* tail in
  // waitFor and replaces entry.lock with its own acquired promise. The finally
  // block resolves acquired so the next waiter can proceed.
  let releaseLock!: () => void;
  const acquired = new Promise<void>((resolve) => (releaseLock = resolve));
  const waitFor = entry.lock;
  entry.lock = acquired;

  try {
    await waitFor;

    // Point the shared logger at this call's progress callback before
    // starting recognition (logger fires synchronously on the worker thread).
    entry.activeCallback = onProgress;

    if (psm !== undefined) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      await (entry.worker as any).setParameters({ tessedit_pageseg_mode: psm });
    }

    const { data } = await entry.worker.recognize(
      image as Parameters<typeof entry.worker.recognize>[0],
    );

    const text = data.text.trim();
    // An unreadable result is no text at all: every caller already answers an empty
    // text with its "no text found" state, instead of filling a text area with noise.
    const readable = isReadableOcr(text, data.confidence);
    return {
      text: readable ? text : '',
      confidence: data.confidence,
      usedFallback,
      unreadable: !readable && text.length > 0,
    };
  } finally {
    if (psm !== undefined) {
      // Reset to AUTO so subsequent calls with this cached worker are unaffected.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      await (entry.worker as any).setParameters({ tessedit_pageseg_mode: PSM_AUTO }).catch(() => {});
    }
    entry.activeCallback = undefined;
    releaseLock();
  }
}
